import React, { useContext, useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { Button, Card, Upload, message, Row, Col, Typography, Image, Select } from "antd";
import { UploadOutlined, ClearOutlined, CheckOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Title } = Typography;

export default function SignatureUpdation() {
    const { authData, backUrl } = useContext(AuthContext);

    const sigRef = useRef({});
    const [preview, setPreview] = useState("");
    const [trimmedDataURL, setTrimmedDataURL] = useState(null);
    const [drawSignature, setDrawSignature] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrganisation, setSelectedOrganisation] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState("");
    const [selectedDesignation, setSelectedDesignation] = useState("");

    const [ishr, setIsHr] = useState(false);

    // ---- APIs ----
    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results);
    };

    const fetchDepartment = async (orgId) => {
        const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDepartments(res.data.results);
    };

    const fetchDesignation = async (depId) => {
        const res = await axios.get(`${backUrl}/api/emp/designations/?department=${depId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDesignations(res.data.results);
    };

    useEffect(() => {
        if (authData?.employee?.is_hr) setIsHr(true);
        fetchOrganisation();
    }, []);

    // ---- Signature Actions ----
    const clearSignature = () => {
        sigRef.current.clear();
        setPreview("");
        setTrimmedDataURL(null);
        setUploadedFile(null);
    };

    const saveSignature = () => {
        const dataURL = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
        setPreview(dataURL);
        setTrimmedDataURL(dataURL);
        setUploadedFile(null);
        message.success("Signature captured!");
    };

    const handleUploadChange = (info) => {
        const file = info.file.originFileObj;
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setPreview(reader.result);
            setUploadedFile(file);
            setTrimmedDataURL(null);
        };
        reader.readAsDataURL(file);
        message.success("Image uploaded!");
    };

    // ---- Final Upload ----
    const uploadSignature = async () => {
        if (!trimmedDataURL && !uploadedFile) {
            message.error("Please draw or upload a signature!");
            return;
        }

        const formData = new FormData();

        // HR can also send organisation, department, designation
        if (ishr) {
            if (!selectedOrganisation || !selectedDepartment || !selectedDesignation) {
                return message.error("Please select Organisation, Department and Designation!");
            }
            formData.append("organisation", selectedOrganisation);
            formData.append("department", selectedDepartment);
            formData.append("designation", selectedDesignation);
        }

        // Add signature
        if (uploadedFile) {
            formData.append("signature", uploadedFile);
        } else {
            const blob = await (await fetch(trimmedDataURL)).blob();
            formData.append("signature", blob, "signature.png");
        }

        try {
            await axios.post(
                `${backUrl}/api/emp/upload-signature/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            message.success("Signature updated successfully!");
        } catch (err) {
            console.error(err);
            message.error("Failed to upload signature");
        }
    };

    return (
        <Card
            style={{
                maxWidth: 700,
                margin: "20px auto",
                padding: 20,
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
        >
            {/* HOD – Switch Button */}
            {authData?.employee?.is_hod && (
                <center>
                    <Button
                        style={{ background: "#0e497b", color: "white" }}
                        onClick={() => setDrawSignature(!drawSignature)}
                    >
                        {drawSignature ? "Upload Signature" : "Draw Signature"}
                    </Button>
                </center>
            )}

            {/* HR Dropdowns */}
            {ishr && (
                <>
                    <Title level={4}>Select Employee Details</Title>

                    {/* Organisation */}
                    <Select
                        placeholder="Select Organisation"
                        style={{ width: "100%", marginBottom: 15 }}
                        value={selectedOrganisation || undefined}
                        onChange={(value) => {
                            setSelectedOrganisation(value);
                            setDepartments([]);
                            setSelectedDepartment("");
                            setDesignations([]);
                            setSelectedDesignation("");
                            fetchDepartment(value);
                        }}
                    >
                        {organisations.map((org) => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>

                    {/* Department */}
                    <Select
                        placeholder="Select Department"
                        style={{ width: "100%", marginBottom: 15 }}
                        value={selectedDepartment || undefined}
                        onChange={(value) => {
                            setSelectedDepartment(value);
                            setDesignations([]);
                            setSelectedDesignation("");
                            fetchDesignation(value);
                        }}
                        disabled={!selectedOrganisation}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {departments.map((d) => (
                            <Select.Option key={d.id} value={d.id}>
                                {d.name}
                            </Select.Option>
                        ))}
                    </Select>

                    {/* Designation */}
                    <Select
                        placeholder="Select Designation"
                        style={{ width: "100%", marginBottom: 15 }}
                        value={selectedDesignation || undefined}
                        onChange={(value) => setSelectedDesignation(value)}
                        disabled={!selectedDepartment}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {designations.map((ds) => (
                            <Select.Option key={ds.id} value={ds.id}>
                                {ds.name}
                            </Select.Option>
                        ))}
                    </Select>
                </>
            )}

            {/* Drawing Canvas */}
            {drawSignature && (
                <>
                    <Title level={4} style={{ textAlign: "center", marginTop: 10 }}>
                        E-Signature Capture
                    </Title>

                    <SignatureCanvas
                        ref={sigRef}
                        penColor="black"
                        backgroundColor="#fff"
                        canvasProps={{
                            width: 600,
                            height: 200,
                            className: "sigCanvas",
                            style: { border: "1px solid #d9d9d9", borderRadius: 8 },
                        }}
                    />

                    <Row justify="center" gutter={10} style={{ marginTop: 15 }}>
                        <Col>
                            <Button icon={<ClearOutlined />} danger onClick={clearSignature}>
                                Clear
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                style={{ background: "#0e497b", color: "white" }}
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={saveSignature}
                            >
                                Save Signature
                            </Button>
                        </Col>
                    </Row>
                </>
            )}

            {/* Upload Image */}
            {!drawSignature && (
                <div style={{ marginTop: 25, textAlign: "center" }}>
                    <Upload accept="image/*" showUploadList={false} onChange={handleUploadChange}>
                        <Button icon={<UploadOutlined />}>Upload Signature Image</Button>
                    </Upload>
                </div>
            )}

            {/* Preview */}
            {preview && (
                <div style={{ textAlign: "center", marginTop: 25 }}>
                    <Title level={5}>Signature Preview:</Title>
                    <Image
                        src={preview}
                        width={300}
                        style={{
                            border: "1px solid #dcdcdc",
                            padding: 8,
                            borderRadius: 8,
                        }}
                    />
                </div>
            )}

            {/* Fix Signature Button */}
            <div style={{ textAlign: "center", marginTop: 30 }}>
                <Button
                    style={{ background: "#0e497b", color: "white" }}
                    type="primary"
                    size="large"
                    onClick={uploadSignature}
                    disabled={!preview}
                >
                    Fix Signature
                </Button>
            </div>
        </Card>
    );
}
